import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { BlocksRenderer } from "../components/BlocksRenderer";
import { useNoticia } from "../hooks/useCms";
import { mediaUrl } from "../lib/strapi";
import type { Noticia } from "../types/cms";
import { NotFoundPage } from "./NotFoundPage";

/**
 * Detalle de noticia: /noticias/:slug
 *
 * Muestra título, fecha, etiqueta, imagen de portada (si la hay),
 * resumen como entradilla y el cuerpo (BlocksRenderer). Si la noticia
 * tiene autor con foto consentida, aparece al pie como crédito.
 */

const etiquetaLabel: Record<NonNullable<Noticia["etiquetas"]>, string> = {
  programa: "Programa",
  evento: "Evento",
  prensa: "Prensa",
  convocatoria: "Convocatoria",
};

export function NoticiaDetallePage() {
  const { slug } = useParams<{ slug: string }>();
  const { data: noticia, isLoading } = useNoticia(slug ?? "");

  if (!slug) return <NotFoundPage />;

  if (isLoading) {
    return (
      <section className="container-emjc py-20">
        <p className="text-gray-700">Cargando…</p>
      </section>
    );
  }

  if (!noticia) return <NotFoundPage />;

  const fechaTxt = new Date(noticia.fechaPublicacion).toLocaleDateString(
    "es-ES",
    { day: "numeric", month: "long", year: "numeric" },
  );
  const etiqueta = noticia.etiquetas ? etiquetaLabel[noticia.etiquetas] : null;
  const portadaSrc =
    mediaUrl(noticia.imagenPortada?.formats?.large?.url) ??
    mediaUrl(noticia.imagenPortada?.url);

  // RGPD: foto del autor solo si consentió. Defensa en profundidad.
  const autor = noticia.autor;
  const autorFoto =
    autor?.consentimientoRgpd && autor.foto?.url
      ? mediaUrl(autor.foto.url)
      : undefined;

  const seoTitle = noticia.seoTitle ?? noticia.titulo;
  const seoDescription =
    noticia.seoDescription ?? noticia.resumen ?? `${noticia.titulo} — Campus EMJC`;

  return (
    <>
      <Helmet>
        <title>{seoTitle} — Campus EMJC</title>
        <meta name="description" content={seoDescription} />
      </Helmet>

      <article className="container-emjc py-20">
        <nav aria-label="Migas de pan" className="text-sm text-gray-700">
          <Link to="/noticias" className="hover:text-brand-orange-deep hover:underline">
            Noticias
          </Link>
          <span aria-hidden="true" className="mx-2">/</span>
          <span aria-current="page">{noticia.titulo}</span>
        </nav>

        <header className="mt-6">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs uppercase tracking-widest text-gray-700">
            <time dateTime={noticia.fechaPublicacion}>{fechaTxt}</time>
            {etiqueta && (
              <span className="rounded-full bg-brand-orange-soft px-2 py-0.5 text-brand-orange-deep">
                {etiqueta}
              </span>
            )}
          </div>
          <h1 className="mt-3 max-w-3xl font-display text-5xl font-semibold leading-tight md:text-6xl">
            {noticia.titulo}
          </h1>
          {noticia.resumen && (
            <p className="mt-6 max-w-3xl text-xl leading-relaxed text-gray-700">
              {noticia.resumen}
            </p>
          )}
        </header>

        {portadaSrc && (
          <figure className="mt-10 overflow-hidden rounded-2xl">
            <img
              src={portadaSrc}
              alt={noticia.imagenPortada?.alternativeText ?? ""}
              className="w-full"
            />
            {noticia.imagenPortada?.alternativeText && (
              <figcaption className="mt-2 text-sm text-gray-700">
                {noticia.imagenPortada.alternativeText}
              </figcaption>
            )}
          </figure>
        )}

        {noticia.cuerpo && noticia.cuerpo.length > 0 && (
          <div className="mt-10">
            <BlocksRenderer blocks={noticia.cuerpo} />
          </div>
        )}

        {autor && (
          <footer className="mt-16 flex items-center gap-4 rounded-2xl border border-gray-300 bg-white p-5">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-brand-orange-soft ring-1 ring-gray-300">
              {autorFoto ? (
                <img
                  src={autorFoto}
                  alt={autor.foto?.alternativeText ?? autor.nombre}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              ) : (
                <span
                  aria-hidden="true"
                  className="font-display text-base font-semibold text-brand-orange-deep"
                >
                  {autor.nombre
                    .split(/\s+/)
                    .slice(0, 2)
                    .map((p) => p[0])
                    .join("")
                    .toUpperCase()}
                </span>
              )}
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-700">
                Autor
              </p>
              <p className="font-display text-base font-semibold">
                {autor.nombre}
              </p>
              {autor.rol && (
                <p className="text-sm text-gray-700">{autor.rol}</p>
              )}
            </div>
          </footer>
        )}

        <div className="mt-12">
          <Link
            to="/noticias"
            className="text-sm font-semibold text-brand-ink hover:text-brand-orange-deep hover:underline"
          >
            ← Volver a todas las noticias
          </Link>
        </div>
      </article>
    </>
  );
}
